/**
 * UserListViewComponent
 * 
 * This component manages the display and operations related to the list of users.
 * Key functionalities include:
 * - Fetching and displaying users for a selected organization.
 * - Providing roles and status of users in a readable format.
 * - Opening dialogs for creating or editing users.
 * - Handling errors gracefully and providing user feedback.
 * 
 * Author: Prajna
 * Date: 2024-12-16
 */
import { DatePipe } from '@angular/common';
import { Component, OnInit, ViewChild } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatTableDataSource } from '@angular/material/table';
import { BaseComponent } from 'src/app/base/base.component';
import { ErrorReportingService } from 'src/app/services/errorReporting/error-reporting.service';
import { UserService } from 'src/app/services/user.service';
import { User } from 'src/app/services/user';
import { CreateUserDialogComponent } from '../create-user-dialog/create-user-dialog.component';
import { MatDialogConfig } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { AuthService } from 'src/app/auth.service';
import { CreateCompanyuserDialogComponent } from '../create-companyuser-dialog/create-companyuser-dialog/create-companyuser-dialog.component';
import { UserChangePasswordComponent } from './user-change-password/user-change-password.component';
import { UserDataService } from 'src/app/services/user-data-service';
import { Router } from '@angular/router';
import { MatPaginator, PageEvent } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { CopilotService } from 'src/app/copilot/services/copilot.service';
import { CopilotUsageToday } from 'src/app/copilot/models/copilot.models';
import { UserUsageHistoryDialogComponent } from './user-usage-history-dialog/user-usage-history-dialog.component';


@Component({
    selector: 'app-user-list-view',
    templateUrl: './user-list-view.component.html',
    styleUrls: ['./user-list-view.component.css'],
    standalone: false
})
export class UserListViewComponent extends BaseComponent implements OnInit {
  users = new MatTableDataSource<User>();
  displayedColumns: string[] = ['userName', 'fullName', 'emailAddress','primaryPhone','title','notes', 'createdDateTime','status', 'copilotUsage', 'actions'];
  hasError: boolean = false;
  role:Number=0;
  organizationId:string='0'
  message:string="";
  usageByUserId: Record<string, CopilotUsageToday> = {};

  @ViewChild(MatPaginator) paginator!: MatPaginator;
  @ViewChild(MatSort) sort!: MatSort;

  constructor(
    errorLogger: ErrorReportingService,
    private dialog: MatDialog,
    private userService: UserService,
    public datePipe: DatePipe,
    private authService: AuthService,
    private snackBar: MatSnackBar,
    private router: Router,
     private userDataService: UserDataService,
     private copilotSvc: CopilotService,

  ) {
    super(errorLogger, dialog);
  }

  ngOnInit(): void {
    this.loadUserData(); // Initial call

    // ✅ Listen for route changes and reload data when the same route is clicked
 //   this.router.events.subscribe(() => {
 //     this.loadUserData();
 //   });


 /*  this.organizationId = this.userDataService.getSelectedOrganization().toString();

    const user = this.authService.getUserDetails();
    if (user) {
      this.role =user.userType ;
     
      if(this.role===1 && this.organizationId==='0')
      {
        this.getUsers('0');
      }
      else
      {
           
        if (this.organizationId) {
          this.getUsers(this.organizationId); // Call your API function
        } else {
          this.message = "No organization assigned to you. Please contact the Super Admin.";
        }
      }
 
    }*/
  }

   ngAfterViewInit(): void {
    this.users.paginator = this.paginator;
    this.users.sort = this.sort;
  }
  
  loadUserData() {
    this.organizationId = this.userDataService.getSelectedOrganization().toString();
    const user = this.authService.getUserDetails();

    if (user) {
      this.role = user.userType;

      if (this.role === 1 && this.organizationId === '0') {
       // this.getUsers('0');
       this.getUsersUnassigned('0')
      } else {
        if (this.organizationId) {
          this.getUsers(this.organizationId);
        } else {
          this.message = 'No organization assigned to you. Please contact the Super Admin.';
        }
      }
  
    }
  }



  /*async getUsers(organizationId: string): Promise<void> {
    try {
      const data = await this.userService.getUserList(organizationId);
      this.users.data = data;  // Assign fetched data to MatTableDataSource
      console.log('Users:', this.users.data);
    } catch (error) {
      console.error('Error fetching users', error);
      this.hasError = true;  // Set hasError if there's an error
    }
  }*/
    async getUsersUnassigned(organizationId: string): Promise<void> {
      try {
        const data = await this.userService.getUnAssignedUserList(organizationId);
        this.users.data = data;  // Assign fetched data to MatTableDataSource
        console.log('Users:', this.users.data);
      } catch (error) {
        console.error('Error fetching users', error);
        this.hasError = true;  // Set hasError if there's an error
      }
    }
    async getUsers(organizationId: string | null | undefined): Promise<void> {
      try {
        if (!organizationId) {
          console.error("Organization ID is undefined or null");
          this.hasError = true;
          return;
        }
    
        const data = await this.userService.getUserList(organizationId);
    
        if (!Array.isArray(data)) {
          console.error("Invalid data received:", data);
          this.hasError = true;
          return;
        }
    
        // Map users and extract roleId for the matching organizationId
        this.users.data = data
          .map(user => {
            const matchingOrg = user.organization.find(org => org.organizationId.toString() === organizationId.toString());
            return matchingOrg ? { ...user, roleId: matchingOrg.roleId } : null;
          })
          .filter(user => user !== null); // Remove null values (users without a matching org)

        console.log("Filtered Users with Role IDs:", this.users.data);
        this.loadUsageForCurrentUsers(organizationId);
      } catch (error) {
        console.error("Error fetching users", error);
        this.hasError = true;
      }
    }

    /** Batch-fetches today's DTR Copilot usage for every user currently in
     * the table (one request, not one per row) — see CopilotService.getUsageBatchToday(). */
    loadUsageForCurrentUsers(organizationId: string): void {
      const userIds = this.users.data.map(u => u.userId).filter(id => id != null);
      if (userIds.length === 0) return;
      this.copilotSvc.getUsageBatchToday(organizationId, userIds).subscribe({
        next: (res) => { this.usageByUserId = res; },
        error: (err) => console.error('Failed to load Copilot usage for users', err),
      });
    }

    getUsagePercent(user: User): number {
      return this.usageByUserId[String(user.userId)]?.usagePercent ?? 0;
    }

    openUsageHistory(user: User): void {
      const dialogConfig = new MatDialogConfig();
      dialogConfig.autoFocus = true;
      dialogConfig.width = '650px';
      dialogConfig.maxWidth = '90vw';
      dialogConfig.data = { userId: user.userId, userName: user.userName, orgId: this.organizationId };
      const dialogRef = this.dialog.open(UserUsageHistoryDialogComponent, dialogConfig);
      dialogRef.afterClosed().subscribe((result) => {
        if (result?.reset) {
          this.loadUsageForCurrentUsers(this.organizationId);
        }
      });
    }

  getOrganizationNames(user: any): string {
    return user.organization && user.organization.length
      ? user.organization.map((org: any) => org.organizationName).join(', ')
      : '';
  }
  getRole(userType: number): string {
    switch (userType) {
        case 1:
            return 'Super Admin';
        case 2:
            return 'Admin';
        case 3:
            return 'Analyst';
         case 4:
              return 'Operator';
            
        default:
            return 'Analyst'; // Fallback in case of unexpected userType
    }
}
getStatus(status: number): string {
  switch (status) {
      case 1:
          return 'Active';
      case 0:
          return 'In-Active';
    
      default:
          return 'Active'; 
  }
}
getRoleColor(): boolean {
  
  const userData = localStorage.getItem('userData');
  if (userData) {
    const user = JSON.parse(userData);
    return user.userType === 1 || user.userType === 2; // Check for roles '1' or '2'
  }
  return false; // Not logged in
}
OpenUserEntryDialog(): void {
  const dialogConfig = new MatDialogConfig();
  
  dialogConfig.disableClose = true;
  dialogConfig.autoFocus = true;
  dialogConfig.width = '600px'; // Adjust the width as needed
  dialogConfig.maxWidth = '90vw'; // Responsive max width

  dialogConfig.data = { title: 'Create User' }; // Pass the title in the data object
  //console.log( "  dialogConfig.data =", dialogConfig.data )
  const dialogRef = this.matDialog.open(CreateUserDialogComponent, dialogConfig);

  dialogRef.afterClosed().subscribe(result => {
    if (result) {
      if (result.status === true) {
        // Show success message
        this.snackBar.open(result.message, 'Close', {
          duration: 3000, // Duration in milliseconds
        });
        //if (this.role === 1) {
          //this.getUsers('0');  // Refresh the user list after successful operation
       // } else {
          this.getUsers(this.organizationId);  // Refresh the user list after successful operation
        //}
       // Refresh user list
      } else if (result.status === false) {
        // Show a warning dialog (not a snackbar) so validation failures such as a
        // duplicate User ID stay visible until the user acknowledges them.
        const errorMessage = result.message || 'Error creating user. Please try again.'; // Use the provided message or a default one
        this.displayWarning(errorMessage, 'Unable to Create User');
      }
    }
  });


  dialogRef.afterClosed().subscribe(result => {
    if (result && result.name) {
      // Handle the result here
    }
  });
}
OpenCompanyUserEntryDialog(): void {
  const dialogConfig = new MatDialogConfig();
  
  dialogConfig.disableClose = true;
  dialogConfig.autoFocus = true;
  dialogConfig.width = '600px'; // Adjust the width as needed
  dialogConfig.maxWidth = '90vw'; // Responsive max width

  dialogConfig.data = { title: 'Create Company User' }; // Pass the title in the data object
  //console.log( "  dialogConfig.data =", dialogConfig.data )
  const dialogRef = this.matDialog.open(CreateCompanyuserDialogComponent, dialogConfig);

  dialogRef.afterClosed().subscribe(result => {
    if (result) {
      if (result.status === true) {
        // Show success message
        this.snackBar.open(result.message, 'Close', {
          duration: 3000, // Duration in milliseconds
        });
        this.getUsers(this.organizationId); // Refresh user list
      } else if (result.status === false) {
        // Show specific error message if provided
        const errorMessage = result.message || 'Error creating user. Please try again.'; // Use the provided message or a default one
        this.snackBar.open(errorMessage, 'Close', {
          duration: 3000, // Duration in milliseconds
        });
      }
    }
  });
  
  
  dialogRef.afterClosed().subscribe(result => {
    if (result && result.name) {
      // Handle the result here
    }
  });
}

async openUserDialog(userId: number, orgLength: number): Promise<void> {
  if (orgLength === 0) {
    // If organization length is 0, open the Company User Edit Dialog
    //console.log("Opening Company User Edit Dialog...");
    await this.OpenCompanyUserEditDialog(userId);  // Your function for company user
  } else {
    // If organization length is greater than 0, open the regular User Edit Dialog
   // console.log("Opening User Edit Dialog...");
    await this.OpenUserEditDialog(userId);  // Your function for regular user
  }
}

async OpenCompanyUserEditDialog(userId: number): Promise<void> {
  // Check if we are in edit mode (based on the presence of userId)
  
  const isEditMode = !!userId;
  const title = isEditMode ? 'Edit User' : 'Create User';

  try {
    let user: User = null;

    // If editing, fetch user details using the userId
    if (isEditMode) {
      user = await this.userService.getUserById(userId);
    }

    const dialogConfig = new MatDialogConfig();
    dialogConfig.disableClose = true;
    dialogConfig.autoFocus = true;
    dialogConfig.width = '600px';  // Adjust width
    dialogConfig.maxWidth = '90vw';  // Make dialog responsive

    dialogConfig.data = { 
      title, 
      user, 
      hideUsername: isEditMode,  // Hide username if editing
      hidePassword: isEditMode   // Hide password if editing
    };

    const dialogRef = this.matDialog.open(CreateCompanyuserDialogComponent, dialogConfig);

    // Wait for the dialog to close and handle the result
    const result = await dialogRef.afterClosed().toPromise();
    console.log("Dialog closed with result:", result);

    if (result) {
      if (result.status === true) {
        this.snackBar.open(result.message, 'Close', {
          duration: 3000,  // Duration in milliseconds
        });
        if (this.role === 1) {
          this.getUsers('0');  // Refresh the user list after successful operation
        } else {
          this.getUsers(this.organizationId);  // Refresh the user list after successful operation
        }
       
      } else if (result.status === false) {
        const errorMessage = result.message || 'Error updating user. Please try again.';
        this.snackBar.open(errorMessage, 'Close', {
          duration: 3000,  // Duration in milliseconds
        });
      }
    }
  } catch (error) {
    console.error('Error fetching user details:', error);
    this.snackBar.open('Error fetching user details. Please try again later.', 'Close', {
      duration: 3000,  // Duration in milliseconds
    });
  }
}
async OpenUserEditDialog(userId: number): Promise<void> {
  // Check if we are in edit mode (based on the presence of userId)
 
  const isEditMode = !!userId;
  const title = isEditMode ? 'Edit User' : 'Create User';

  try {
    let user: User = null;

    // If editing, fetch user details using the userId
    if (isEditMode) {
      user = await this.userService.getUserById(userId);
    }

    const dialogConfig = new MatDialogConfig();
    dialogConfig.disableClose = true;
    dialogConfig.autoFocus = true;
    dialogConfig.width = '600px';  // Adjust width
    dialogConfig.maxWidth = '90vw';  // Make dialog responsive

    dialogConfig.data = { 
      title, 
      user, 
      hideUsername: isEditMode,  // Hide username if editing
      hidePassword: isEditMode   // Hide password if editing
    };

    const dialogRef = this.matDialog.open(CreateUserDialogComponent, dialogConfig);

    // Wait for the dialog to close and handle the result
    const result = await dialogRef.afterClosed().toPromise();
    console.log("Dialog closed with result:", result);

    if (result) {
      if (result.status === true) {
        this.snackBar.open(result.message, 'Close', {
          duration: 3000,  // Duration in milliseconds
        });
      //  if (this.role === 1) {
       //   this.getUsers('0');  // Refresh the user list after successful operation
      //  } else {
          this.getUsers(this.organizationId);  // Refresh the user list after successful operation
     //   }
      } else if (result.status === false) {
        const errorMessage = result.message || 'Error updating user. Please try again.';
        this.snackBar.open(errorMessage, 'Close', {
          duration: 3000,  // Duration in milliseconds
        });
      }
    }
  } catch (error) {
    console.error('Error fetching user details:', error);
    this.snackBar.open('Error fetching user details. Please try again later.', 'Close', {
      duration: 3000,  // Duration in milliseconds
    });
  }
}


deleteUser(userId: string): void {
  // Replace the confirm with a custom confirmation dialog
  this.showConfirmDialog(
    'Delete User?', // Title of the dialog
    'Are you sure you want to delete this user permanently?', // Message of the dialog
    async (result) => {
      if (result) { // Proceed only if the user confirms (result is true)
        // Call the service to delete the user by their ID
        this.userService.deleteUser(userId).subscribe(
          (response) => {
            // Handle success, refresh the table or notify the user
            console.log('User deleted successfully');

            // Show success message from the response
            this.snackBar.open(response.message || 'User deleted successfully', 'Close', {
              duration: 3000, // Duration for the snackbar
            });
            
          //  if (this.role === 1) {
            //  this.getUsers('0');  // Refresh the user list after successful operation
          //  } else {
              this.getUsers(this.organizationId);  // Refresh the user list after successful operation
          //  }
          },
          (error) => {
            // Handle error (e.g., show an error message)
            console.error('Failed to delete user:', error);

            // Show the error message from the response or a default one
            const errorMessage = error?.error?.message || 'Failed to delete user. Please try again.';
            this.snackBar.open(errorMessage, 'Close', {
              duration: 3000, // Duration for the snackbar
            });
          }
        );
      }
    }
  );
}
async duplicateUser(userId: number): Promise<void> {
  try {
    // Fetch the user details by their ID using async/await
    const user = await this.userService.getUserById(userId);

    // Create a copy of the fetched user data and modify the username to make it unique
    const duplicatedUser = { ...user }; // Clone the user data
    duplicatedUser.userName = this.generateUniqueUsername(user.userName);

    // Generate a password for the duplicated user
    const currentYear = new Date().getFullYear(); // Get the current year
    duplicatedUser.password = `EvidantDTR${currentYear}`;

    // Call the createUser method to create the duplicated user with the new username
    const createdUser = await this.userService.createUser(duplicatedUser);

    // Notify the user of success
    this.snackBar.open('User duplicated successfully!', 'Close', {
      duration: 3000, // Duration for the snackbar
    });

    // Optionally, you can refresh the user list or take other actions after successful creation
    if (this.role === 1) {
      this.getUsers('0');  // Refresh the user list after successful operation
    } else {
      this.getUsers(this.organizationId);  // Refresh the user list after successful operation
    }

  } catch (error) {
    // Handle any error fetching the original user data or creating the duplicated user
    console.error('Error duplicating user:', error);
    this.snackBar.open('Error duplicating user. Please try again.', 'Close', {
      duration: 3000, // Duration for the snackbar
    });
  }
}

// Unique Username Generator (Example with Random Number)
generateUniqueUsername(username: string): string {
  const randomNum = Math.floor(1000 + Math.random() * 9000);
  return `${username}_${randomNum}`;
}

generateUniqueUsernameWithRandomChars(username: string, length: number = 4): string {
  const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
  let randomString = '';
  
  for (let i = 0; i < length; i++) {
    const randomIndex = Math.floor(Math.random() * chars.length);
    randomString += chars[randomIndex];
  }
  
  return `${username}_${randomString}`;
}
 // Method to check if user is logged in and has role 1 or 2
 isLoggedInAndHasRoleOrg(): boolean {
  const userData = localStorage.getItem('userData');
  //console.log(userData);
  if (userData) {
    const user = JSON.parse(userData);
    return user.userType === 1 || user.userType === 2; // Check for both roles 1 and 2
  }
  return false; // Not logged in
}
userChangePasswordDialog(userID: string,userName: string): void {
    const dialogConfig = new MatDialogConfig();
    
    dialogConfig.disableClose = true;
    dialogConfig.autoFocus = true;
    dialogConfig.width = '600px'; // Adjust the width as needed
    dialogConfig.maxWidth = '90vw'; // Responsive max width
   // console.log("userName===",userName);
    //dialogConfig.data = { title: 'Manage Users' }; // Pass the title in the data object
    dialogConfig.data = { 
      title: 'User Change PAssword', 
      userID,
      userName
    };
    //console.log( "  dialogConfig.data =", dialogConfig.data )
    const dialogRef = this.matDialog.open(UserChangePasswordComponent, dialogConfig);
  
    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        if (result.status === true) {
          // Show success message
          this.snackBar.open(result.message, 'Close', {
            duration: 3000, // Duration in milliseconds
          });
          this.getUsers(this.organizationId); // Refresh the user list // Refresh user list
        } else if (result.status === false) {
          // Show specific error message if provided
          const errorMessage = result.message || 'Error creating Users. Please try again.'; // Use the provided message or a default one
          this.snackBar.open(errorMessage, 'Close', {
            duration: 3000, // Duration in milliseconds
          });
        }
      }
    });
    
    
    dialogRef.afterClosed().subscribe(result => {
      if (result && result.name) {
        // Handle the result here
      }
    });
  }

  getUserRoleTooltip(user: any): string {
    if (user.userType === 1) {
      return 'Super Admin';
    }
  
    switch (user.userType) {
      case 2: return 'Admin';
      case 3: return 'Analyst';
      case 4: return 'Operator';
      default: return 'Unknown Role';
    }
  }
  
  getUserRoleColor(user: any): string {
    if (user.userType === 1) {
      return '#C00000'; // Super Admin
    }
    switch (user.userType) {
      case 2: return '#C09100'; // Admin
      case 3: return '#5B9BD5'; // Analyst
      case 4: return '#00CC99'; // Operator
      default: return '#808080'; // Unknown role (gray)
    }
  }
   applyFilter(event: Event): void {
  const filterValue = (event.target as HTMLInputElement).value;
  this.users.filter = filterValue.trim().toLowerCase();

  // Reset to the first page after filter
  if (this.users.paginator) {
    this.users.paginator.firstPage();
  }
}
  
}
